import { API_BASE } from "./config";

/**
 * Sign-in state.
 *
 * PLAN §0.5: the SPA asks `GET /api/auth/session` on boot and bounces to `/api/auth/login` when it
 * is signed out, **carrying a `next` param so the tray agent's capture deep-link survives landing
 * signed out**. Without that, a capture arriving at `/?q=…` while the cookie has expired loses the
 * sentence on the way through Google.
 */
export interface SessionState {
  authenticated: boolean;
  email: string | null;
  /** False when the server has no OAuth configured — a fresh clone runs with no sign-in at all. */
  required: boolean;
}

export async function getSession(): Promise<SessionState> {
  const res = await fetch(`${API_BASE}/api/auth/session`, { cache: "no-store" });
  if (!res.ok) throw new Error(`Could not read the session (${res.status})`);
  return res.json();
}

/** Where to land after signing in: this page, query string and all. */
export function currentNext(location: Location = window.location): string {
  return `${location.pathname}${location.search}` || "/";
}

export function loginUrl(next: string = currentNext()): string {
  return `${API_BASE}/api/auth/login?next=${encodeURIComponent(next)}`;
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/api/auth/logout`, { method: "POST" });
}

/**
 * Guard against a redirect loop.
 *
 * If the cookie never sticks — a misconfigured redirect URI, a clock skew, third-party cookie
 * blocking — an unconditional bounce would ping-pong the browser between here and Google forever.
 * One automatic attempt per tab; after that the user gets a button and an explanation instead.
 */
const ATTEMPTED_KEY = "nihongonator:signin-attempted";

export function hasAutoRedirected(): boolean {
  try {
    return sessionStorage.getItem(ATTEMPTED_KEY) === "1";
  } catch {
    // Private mode with storage disabled: treat as "already tried" so we never loop.
    return true;
  }
}

export function markAutoRedirected(): void {
  try {
    sessionStorage.setItem(ATTEMPTED_KEY, "1");
  } catch {
    /* nothing to do — the check above fails closed */
  }
}

export function clearAutoRedirected(): void {
  try {
    sessionStorage.removeItem(ATTEMPTED_KEY);
  } catch {
    /* as above */
  }
}

/**
 * A mid-session 401 — the cookie expired under a long-lived tab. AuthGate only checks at boot, so
 * without this the app degrades to per-action error banners until a manual reload. One automatic
 * bounce per tab (the same guard as boot); after that the 401 surfaces as an ordinary error. The
 * `next` is the current PAGE, not the API path the guard saw — coming back should land you where
 * you were, query string and all.
 */
export function bounceIfUnauthorized(res: Response): void {
  if (res.status !== 401 || hasAutoRedirected()) return;
  markAutoRedirected();
  window.location.assign(loginUrl());
}
